import React from 'react';
import { useSiteSettings } from '../context/SiteSettingsContext';

function TermsOfService() {
  const { settings } = useSiteSettings();
  const churchName = settings.church_name || 'Coos Bay Cornerstone';
  const churchEmail = settings.email || 'info@coosbaycornerstone.com';
  const churchPhone = settings.phone || '(541) 808-9393';
  const churchAddress = `${settings.address || '63748 Harriet Road'}, ${settings.city_state_zip || 'Coos Bay, Oregon 97420'}`;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="card p-8 md:p-12">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Terms of Service
          </h1>
          <p className="text-slate-500 mb-8">Last updated: February 2025</p>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Agreement to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                By accessing and using the {churchName} website ("Site"), you agree to be bound by these Terms of Service. 
                If you do not agree with any part of these terms, please do not use our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Use of the Website</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                This website is provided by {churchName} for informational and community purposes. You agree to use this Site only for lawful purposes and in accordance with these Terms.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Use the Site in any way that violates any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to any portion of the Site</li>
                <li>Use the Site to transmit any harmful, threatening, or offensive content</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Interfere with the proper functioning of the Site</li>
                <li>Collect or harvest any information from the Site without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>User Accounts</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                When you create an account on our Site, you are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and current information</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                We reserve the right to suspend or terminate accounts that violate these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>SMS Communications</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                By providing your phone number and opting in to SMS communications, you consent to receive text messages from {churchName}. 
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Message frequency varies based on church activities and your subscription preferences</li>
                <li>Message and data rates may apply depending on your mobile carrier</li>
                <li>You can opt out at any time by replying STOP to any message</li>
                <li>Reply HELP for assistance or contact us directly</li>
                <li>SMS consent is not required for church membership or participation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Prayer Requests</h2>
              <p className="text-slate-700 leading-relaxed">
                When you submit a prayer request through our Site, you understand that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
                <li>Your request may be shared with our prayer team and congregation unless marked as confidential</li>
                <li>We will treat all requests with respect and discretion</li>
                <li>Prayer requests are not a substitute for professional medical, legal, or counseling services</li>
                <li>We reserve the right to not publish requests that are inappropriate or unrelated to prayer</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Donations and Giving</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                All donations made through our Site are:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Voluntary contributions to support the ministry of {churchName}</li>
                <li>Generally non-refundable unless made in error</li>
                <li>Tax-deductible to the extent allowed by law (consult your tax advisor)</li>
                <li>Processed securely through our third-party payment providers</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                We will provide donation receipts for your tax records. Please contact us if you need assistance with any donation matter.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Intellectual Property</h2>
              <p className="text-slate-700 leading-relaxed">
                The content on this Site, including text, graphics, logos, images, and software, is the property of {churchName} 
                or our content providers and is protected by copyright and other intellectual property laws. You may not reproduce, 
                distribute, or create derivative works without our written permission, except for personal, non-commercial use.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Third-Party Links</h2>
              <p className="text-slate-700 leading-relaxed">
                Our Site may contain links to third-party websites. We are not responsible for the content, privacy practices, 
                or terms of service of these external sites. We encourage you to review the policies of any third-party site you visit.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Disclaimer of Warranties</h2>
              <p className="text-slate-700 leading-relaxed">
                This Site is provided "as is" without warranties of any kind, either express or implied. We do not warrant 
                that the Site will be uninterrupted, error-free, or free of viruses or other harmful components. Your use 
                of the Site is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed">
                To the fullest extent permitted by law, {churchName} shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages arising out of your use of or inability to use the Site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Indemnification</h2>
              <p className="text-slate-700 leading-relaxed">
                You agree to indemnify and hold harmless {churchName}, its staff, volunteers, and affiliates from any 
                claims, damages, or expenses arising from your violation of these Terms or your use of the Site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Changes to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
                to the Site. Your continued use of the Site after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Governing Law</h2>
              <p className="text-slate-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Oregon, 
                without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Contact Us</h2>
              <p className="text-slate-700 leading-relaxed">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-6 bg-slate-50 rounded-lg">
                <p className="text-slate-700 font-semibold">{churchName}</p>
                <p className="text-slate-700">{churchAddress}</p>
                <p className="text-slate-700">Phone: {churchPhone}</p>
                <p className="text-slate-700">Email: {churchEmail}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
